
import * as React from "react";
import type { Initiative, Person } from "@/lib/types";
import { QUARTERS, STATUS_LABELS, ALL_TAGS, tagNum, TEAMS, normalizeQuarterId } from "@/lib/constants";
import { Icon } from "./icons";
import { Popover } from "./primitives";
import {
  toggleTeam, togglePod, teamRowState, podIsOn, teamFilterLabel,
} from "@/lib/team-filter";
import { NO_WORKSTREAM, workstreamNames } from "@/lib/group-by";
import { toggleQuarterRange, zoomLabel } from "@/lib/timeline-window";

export type Filters = {
  quarter: string[];
  status: string[];
  dri: string[];
  pillar: string[];
  team: string[];
  // Pods ride under the Team chip — one two-level checklist, not a sixth
  // chip. See lib/team-filter.ts for the selection semantics.
  pod: string[];
  // Workstream names (case-insensitive), or NO_WORKSTREAM for unassigned.
  workstream: string[];
};

export const EMPTY_FILTERS: Filters = {
  quarter: [],
  status: [],
  dri: [],
  pillar: [],
  team: [],
  pod: [],
  workstream: [],
};

// Chips rendered in the bar. `pod` has no chip of its own.
type FilterKey = Exclude<keyof Filters, "pod">;

type FilterDef = {
  label: string;
  options: { id: string; label: string; swatch?: string }[];
};

// Above this many people the Owner popover gets a type-to-filter box.
const OWNER_SEARCH_THRESHOLD = 8;

export function FilterBar({
  filters,
  setFilters,
  roster,
  initiatives,
  currentUserId = null,
}: {
  filters: Filters;
  setFilters: (f: Filters) => void;
  roster: Person[];
  initiatives: Initiative[];
  /** Signed-in user's roster id (null when auth is off) — pinned atop the Owner list. */
  currentUserId?: string | null;
}) {
  const [openKey, setOpenKey] = React.useState<FilterKey | null>(null);
  const [ownerQuery, setOwnerQuery] = React.useState("");
  const close = React.useCallback(() => setOpenKey(null), []);
  // A fresh open starts with an empty search.
  React.useEffect(() => { if (openKey !== "dri") setOwnerQuery(""); }, [openKey]);
  const refs = React.useRef<Record<FilterKey, HTMLButtonElement | null>>({
    quarter: null, status: null, dri: null, pillar: null, team: null, workstream: null,
  });

  // Per-option counts across all (unfiltered) initiatives, so the dropdowns
  // double as a status-of-the-portfolio glance.
  const countFor = React.useCallback((key: FilterKey, id: string) => {
    switch (key) {
      case "quarter": return initiatives.filter(i => normalizeQuarterId(i.targetQ) === id).length;
      case "status": return initiatives.filter(i => i.status === id).length;
      case "dri": return initiatives.filter(i => i.driId === id).length;
      case "pillar": return initiatives.filter(i => i.pillarIds.includes(id)).length;
      case "team": return initiatives.filter(i => i.teamId === id).length;
      case "workstream": return initiatives.filter(i => {
        const w = i.workstream?.trim().toLowerCase();
        return id === NO_WORKSTREAM ? !w : w === id.toLowerCase();
      }).length;
    }
  }, [initiatives]);
  const podCount = React.useCallback(
    (podId: string) => initiatives.filter(i => i.podId === podId).length,
    [initiatives],
  );

  const FILTER_OPTIONS: Record<FilterKey, FilterDef> = {
    quarter: {
      label: "Quarter",
      // Year-qualified across all fiscal years ("FY26 Q1" … "FY27 Q4").
      options: QUARTERS.map(q => ({ id: q.id, label: `${q.year} ${q.label}` })),
    },
    status: {
      label: "Status",
      options: (Object.entries(STATUS_LABELS) as [string, string][])
        .map(([id, label]) => ({ id, label, swatch: `var(--status-${id})` })),
    },
    dri: {
      label: "Owner",
      options: roster.map(p => ({ id: p.id, label: p.name })),
    },
    pillar: {
      // Tags — the admin-curated vocabulary. Everyone can filter by any
      // current tag; a retired tag stays listed while initiatives still carry it.
      label: "Tag",
      options: ALL_TAGS
        .filter(p => !p.retired || countFor("pillar", p.id) > 0)
        .map(p => {
          const label = tagNum(p) ? `${tagNum(p)} · ${p.short}` : p.short;
          return { id: p.id, label: p.retired ? `${label} (retired)` : label };
        }),
    },
    team: {
      label: "Team",
      options: TEAMS.map(t => ({ id: t.id, label: t.name })),
    },
    workstream: {
      label: "Workstream",
      options: [
        ...workstreamNames(initiatives).map(n => ({ id: n, label: n })),
        { id: NO_WORKSTREAM, label: "No workstream" },
      ],
    },
  };

  const order: FilterKey[] = ["quarter", "status", "dri", "pillar", "team", "workstream"];

  // The Team chip carries both lists; `has` and its label must see the pods too.
  const hasValue = (key: FilterKey) =>
    filters[key].length > 0 || (key === "team" && filters.pod.length > 0);

  const labelFor = (key: FilterKey) => {
    const vals = filters[key];
    const def = FILTER_OPTIONS[key];
    if (key === "team") return teamFilterLabel({ team: filters.team, pod: filters.pod }, def.label);
    if (!vals.length) return def.label;
    if (key === "quarter") return `${def.label}: ${zoomLabel(vals) || vals.length + " selected"}`;
    if (vals.length === 1) {
      const opt = def.options.find(o => o.id === vals[0]);
      return `${def.label}: ${opt?.label ?? vals[0]}`;
    }
    return `${def.label}: ${vals.length} selected`;
  };

  const toggleVal = (key: FilterKey, id: string) => {
    if (key === "quarter") {
      setFilters({ ...filters, quarter: toggleQuarterRange(filters.quarter, id) });
      return;
    }
    const cur = filters[key];
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    setFilters({ ...filters, [key]: next });
  };

  const clearFilters = (key: FilterKey) =>
    setFilters(key === "team" ? { ...filters, team: [], pod: [] } : { ...filters, [key]: [] });

  const clearKey = (e: React.MouseEvent, key: FilterKey) => {
    e.stopPropagation();
    clearFilters(key);
  };

  const onTeamRow = (teamId: string) => {
    const next = toggleTeam({ team: filters.team, pod: filters.pod }, teamId);
    setFilters({ ...filters, ...next });
  };
  const onPodRow = (podId: string) => {
    const next = togglePod({ team: filters.team, pod: filters.pod }, podId);
    setFilters({ ...filters, ...next });
  };

  // Team ▸ Pod checklist. Team rows toggle the whole team (pods read as
  // implied); pod rows toggle one pod, and clicking a pod under a selected
  // team narrows to "every other pod" in one click.
  const renderTeamTree = () => {
    const sel = { team: filters.team, pod: filters.pod };
    return TEAMS.map((t, idx) => {
      const state = teamRowState(sel, t.id);
      return (
        <React.Fragment key={t.id}>
          {idx > 0 && <div className="popover-divider" />}
          <button
            className={`popover-item popover-item--team ${state === "on" ? "is-on" : ""} ${state === "partial" ? "is-partial" : ""}`}
            type="button"
            role="menuitemcheckbox"
            data-pop-item
            aria-checked={state === "on" ? true : state === "partial" ? "mixed" : false}
            onClick={() => onTeamRow(t.id)}
          >
            <span className="check"><Icon name={state === "partial" ? "minus" : "check"} size={12} /></span>
            <span>{t.name}</span>
            <span className="opt-count">{countFor("team", t.id)}</span>
          </button>
          {t.pods.map(p => {
            const on = podIsOn(sel, p.id);
            const n = podCount(p.id);
            return (
              <button
                key={p.id}
                className={`popover-item popover-item--pod ${on ? "is-on" : ""} ${state === "on" ? "is-implied" : ""} ${n === 0 ? "is-zero" : ""}`}
                type="button"
                role="menuitemcheckbox"
                data-pop-item
                aria-checked={on}
                onClick={() => onPodRow(p.id)}
              >
                <span className="check"><Icon name="check" size={12} /></span>
                <span>{p.name}</span>
                <span className="opt-count">{n}</span>
              </button>
            );
          })}
        </React.Fragment>
      );
    });
  };

  // Owner rows: the signed-in user is pinned to the top (when auth resolved
  // them to a roster entry), then everyone else, both narrowed by the
  // type-to-filter box. Filtering matches on name only.
  const renderOwnerRows = () => {
    const q = ownerQuery.trim().toLowerCase();
    const matches = (p: Person) => !q || p.name.toLowerCase().includes(q);
    const me = currentUserId ? roster.find(p => p.id === currentUserId) : undefined;
    const rest = roster.filter(p => p.id !== me?.id && matches(p));
    const showMe = me && matches(me);
    if (!showMe && rest.length === 0) {
      return (
        <div className="popover-item popover-item--empty">
          {roster.length === 0 ? "No people yet" : `No one matches “${ownerQuery.trim()}”`}
        </div>
      );
    }
    const row = (p: Person, you: boolean) => {
      const on = filters.dri.includes(p.id);
      return (
        <button
          key={p.id}
          type="button"
          role="menuitemcheckbox"
          data-pop-item
          aria-checked={on}
          className={`popover-item ${on ? "is-on" : ""}`}
          onClick={() => toggleVal("dri", p.id)}
        >
          <span className="check"><Icon name="check" size={12} /></span>
          <span>{p.name}</span>
          {you && <span className="opt-you">you</span>}
          <span className="opt-count">{countFor("dri", p.id)}</span>
        </button>
      );
    };
    return (
      <>
        {showMe && row(me, true)}
        {showMe && rest.length > 0 && <div className="popover-divider" />}
        {rest.map(p => row(p, false))}
      </>
    );
  };

  const renderFlatRows = (key: FilterKey) => {
    const def = FILTER_OPTIONS[key];
    if (def.options.length === 0) {
      return <div className="popover-item popover-item--empty">No options yet</div>;
    }
    return def.options.map(opt => {
      const on = filters[key].includes(opt.id);
      return (
        <button
          key={opt.id}
          type="button"
          role="menuitemcheckbox"
          data-pop-item
          aria-checked={on}
          className={`popover-item ${on ? "is-on" : ""}`}
          onClick={() => toggleVal(key, opt.id)}
        >
          <span className="check"><Icon name="check" size={12} /></span>
          {opt.swatch && <span className="swatch" style={{ background: opt.swatch }} />}
          <span>{opt.label}</span>
          <span className="opt-count">{countFor(key, opt.id)}</span>
        </button>
      );
    });
  };

  return (
    <>
      {order.map(key => {
        const has = hasValue(key);
        return (
          <button
            key={key}
            ref={el => { refs.current[key] = el; }}
            type="button"
            className={`filter-chip ${has ? "has-value" : ""}`}
            data-tour={`filter-${key}`}
            aria-haspopup="menu"
            aria-expanded={openKey === key}
            onClick={() => setOpenKey(openKey === key ? null : key)}
          >
            <Icon name="filter" size={11} /> {labelFor(key)}
            {has && (
              <span
                className="x"
                role="button"
                tabIndex={0}
                aria-label={`Clear ${labelFor(key)} filter`}
                onClick={(e) => clearKey(e, key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    clearKey(e as unknown as React.MouseEvent, key);
                  }
                }}
              >
                <Icon name="x" size={10} />
              </span>
            )}
          </button>
        );
      })}
      {order.map(key => (
        <Popover
          key={`pop-${key}`}
          open={openKey === key}
          anchor={refs.current[key]}
          onClose={close}
          maxHeight={key === "team" ? 600 : 320}
        >
          <div className="popover-head" role="presentation">
            <div className="popover-section">{FILTER_OPTIONS[key].label}</div>
            {key === "quarter" && (
              <div className="popover-hint">Pick a quarter, or a first and last quarter for a range.</div>
            )}
            {key === "dri" && roster.length > OWNER_SEARCH_THRESHOLD && (
              <input
                className="popover-search"
                type="search"
                placeholder="Find a person"
                aria-label="Find a person"
                autoComplete="off"
                value={ownerQuery}
                onChange={e => setOwnerQuery(e.target.value)}
              />
            )}
          </div>
          <div role="menu" aria-label={`${FILTER_OPTIONS[key].label} filter`}>
            {key === "team" ? renderTeamTree() : key === "dri" ? renderOwnerRows() : renderFlatRows(key)}
          </div>
          <div className="popover-foot" role="presentation">
            {hasValue(key) && (
              <>
                <div className="popover-divider" />
                <button type="button" data-pop-item className="popover-item popover-item--clear" onClick={() => clearFilters(key)}>
                  <span className="check" style={{ visibility: "hidden" }} />
                  <span>Clear</span>
                </button>
              </>
            )}
          </div>
        </Popover>
      ))}
    </>
  );
}
